import { Run, RunFilters, RunCreate } from '@/types';
import { apiClient } from './client';
import { logger } from '@/logging';

/**
 * Validate run status
 */
function validateRunStatus(status: string): void {
  const validStatuses = ['running', 'completed', 'failed', 'cancelled'];
  if (!validStatuses.includes(status)) {
    throw new Error(
      `Invalid run status: ${status}. Must be one of: ${validStatuses.join(', ')}`
    );
  }
}

/**
 * Runs API service
 */
export const runsApi = {
  /**
   * Start a new run
   */
  async start(connectorId: string, fullResync = false): Promise<Run> {
    const payload: RunCreate = { connectorId, fullResync };
    logger.info('API: Starting run', { connectorId, fullResync });

    const { data } = await apiClient.post<Run>('/runs', payload);

    logger.info('API: Run created', {
      runId: data.id,
      status: data.status,
      connectorId,
    });

    return data;
  },

  /**
   * Get a single run by ID
   */
  async get(id: string): Promise<Run> {
    logger.debug('API: Fetching run', { runId: id });

    const { data } = await apiClient.get<Run>(`/runs/${id}`);

    logger.debug('API: Run fetched', {
      runId: id,
      status: data.status,
      progress: data.progress,
    });

    return data;
  },

  /**
   * List runs with optional filters
   */
  async list(filters?: RunFilters): Promise<Run[]> {
    // Validate status filter if provided
    if (filters?.status) {
      validateRunStatus(filters.status);
    }

    const { data } = await apiClient.get<Run[]>('/runs', { params: filters });

    logger.debug('API: Listed runs', {
      count: data.length,
      filters,
      runIds: data.slice(0, 5).map(r => r.id), // Log first 5 IDs only
    });

    return data;
  },

  /**
   * Cancel a running run
   */
  async cancel(id: string): Promise<void> {
    logger.info('API: Cancelling run', { runId: id });

    await apiClient.delete(`/runs/${id}`);

    logger.info('API: Run cancelled', { runId: id });
  },

  /**
   * Get run logs
   */
  async getLogs(
    id: string,
    limit = 100
  ): Promise<{
    logs: Array<{
      timestamp: string;
      level: string;
      message: string;
    }>;
  }> {
    const { data } = await apiClient.get(`/runs/${id}/logs`, {
      params: { limit },
    });
    return data;
  },

  /**
   * Get failed documents for a run
   */
  async getFailedDocuments(id: string): Promise<
    Array<{
      documentId: string;
      documentName: string;
      error: string;
      timestamp: string;
      webUrl?: string;
      stage?: string;
      attemptCount?: number;
    }>
  > {
    const { data } = await apiClient.get(`/runs/${id}/failures`);
    return data;
  },

  /**
   * Retry failed documents
   */
  async retryFailedDocuments(
    id: string,
    documentIds?: string[]
  ): Promise<{
    retryRunId: string;
    documentsQueued: number;
    details: {
      documentFailures: number;
      chunkFailures: number;
    };
  }> {
    const { data } = await apiClient.post(`/runs/${id}/retry`, documentIds || null);
    return data;
  },


  /**
   * Get detailed progress for an active run
   * Only available for runs with status: running or processing
   */
  async getProgress(id: string): Promise<{
    runId: string;
    status: string;
    stageCounts: Record<string, Record<string, number>>;
    chunkProgress?: {
      totalChunks: number;
      docsWithChunks: number;
      chunksByStage: Record<string, number>;
      chunkPercentages: Record<string, number>;
    };
    isComplete: boolean;
    hasFailures: boolean;
    failedDocs: number;
    failedChunks: number;
    lastUpdated: string;
  }> {
    logger.debug('API: Fetching run progress', { runId: id });

    const { data } = await apiClient.get(`/runs/${id}/progress`);

    logger.debug('API: Run progress fetched', {
      runId: id,
      status: data.status,
      isComplete: data.isComplete,
    });

    return data;
  },
};
